import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Swords, Trophy, Loader2, Check, X, Zap, ArrowLeft, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Btn from "../components/Btn";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { QStimulus, OptMedia, hasMedia } from "../components/QMedia";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export default function Arena() {
  const { user, refresh } = useAuth();
  const [phase, setPhase] = useState("idle"); // idle | connecting | waiting | match | reveal | result
  const [opponent, setOpponent] = useState(null);
  const [q, setQ] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [total, setTotal] = useState(5);
  const [selected, setSelected] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [scores, setScores] = useState({ you: 0, opp: 0 });
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [exam, setExam] = useState("CPNS");
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const finishedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startCountdown = (secs) => {
    setCountdown(Math.round(secs));
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
    }, 1000);
  };

  const findMatch = async () => {
    setPhase("connecting");
    setResult(null); setScores({ you: 0, opp: 0 });
    finishedRef.current = false;
    let token;
    try {
      const { data } = await api.get("/auth/ws-token");
      token = data.token;
    } catch { setPhase("idle"); return; }
    const ws = new WebSocket(`${WS_BASE}/api/ws/arena?token=${token}&exam=${exam}`);
    wsRef.current = ws;
    ws.onopen = () => setPhase("waiting");
    ws.onclose = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      setPhase((p) => {
        if (!finishedRef.current && p !== "result" && p !== "idle") {
          toast.error("Koneksi arena terputus. Coba lagi.");
          return "idle";
        }
        return p;
      });
    };
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === "waiting") setPhase("waiting");
      else if (msg.type === "match_found") { setOpponent(msg.opponent); setTotal(msg.total); setPhase("match"); }
      else if (msg.type === "question") {
        setQ(msg.question); setQIndex(msg.index); setTotal(msg.total);
        setSelected(null); setReveal(null); setPhase("match"); startCountdown(msg.deadline);
      } else if (msg.type === "reveal") {
        setReveal(msg);
        setScores((prev) => ({ you: msg.you.score, opp: prev.opp + (msg.opp.correct ? 1 : 0) }));
        if (timerRef.current) clearInterval(timerRef.current);
        setPhase("reveal");
      } else if (msg.type === "result") {
        finishedRef.current = true;
        setResult(msg); setPhase("result"); cleanup(); refresh();
      } else if (msg.type === "error") { setPhase("idle"); cleanup(); }
    };
  };

  const answer = (key) => {
    if (selected || reveal) return;
    setSelected(key);
    wsRef.current?.send(JSON.stringify({ type: "answer", index: qIndex, answer: key }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2"><Swords className="w-7 h-7 text-su-primary" /> Arena 1v1</h1>
          <p className="text-su-muted font-medium">Adu cepat, menang, naik ELO! <span className="font-heading font-black text-su-primary">{user?.elo} ELO</span></p>
        </div>
      </div>

      {phase === "idle" && (
        <div className="card-chunky p-8 text-center max-w-lg mx-auto">
          <motion.div animate={{ rotate: [0, -8, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="w-24 h-24 rounded-3xl bg-su-primary/15 grid place-items-center mx-auto mb-4">
            <Swords className="w-12 h-12 text-su-primary" />
          </motion.div>
          <h2 className="font-heading font-black text-2xl mb-2">Siap bertarung?</h2>
          <p className="text-su-muted font-medium mb-5">5 soal kilat melawan lawan online. Jawaban benar terbanyak menang. Kalau tak ada lawan dalam 8 detik, kamu dilawankan Bot Latih.</p>
          <div className="mb-5">
            <p className="text-xs font-heading font-black uppercase text-su-muted mb-2">Pilih Jenis Soal</p>
            <div className="grid grid-cols-2 gap-2" data-testid="arena-exam-picker">
              {["CPNS", "SNBT"].map((ex) => (
                <button key={ex} data-testid={`arena-exam-${ex}`} onClick={() => setExam(ex)}
                  className={`py-3 rounded-2xl font-heading font-black border-2 transition-all ${exam === ex ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
          <Btn data-testid="arena-find" onClick={findMatch} size="lg" className="w-full">Cari Lawan {exam} ⚔️</Btn>
        </div>
      )}

      {(phase === "connecting" || phase === "waiting") && (
        <div className="card-chunky p-10 text-center max-w-lg mx-auto">
          <Loader2 className="w-14 h-14 text-su-primary animate-spin mx-auto mb-4" />
          <h2 className="font-heading font-black text-xl">{phase === "connecting" ? "Menghubungkan..." : "Mencari lawan..."}</h2>
          <p className="text-su-muted font-medium">Bersiaplah, pertarungan segera dimulai!</p>
        </div>
      )}

      {(phase === "match" || phase === "reveal") && (
        <div className="max-w-2xl mx-auto space-y-5">
          {/* VS header */}
          <div className="card-chunky p-4 flex items-center justify-between">
            <PlayerChip name={user?.name} score={scores.you} side="you" />
            <div className="text-center">
              <div className="font-heading font-black text-2xl text-su-danger">VS</div>
              <div className="text-xs font-bold text-su-muted">Soal {qIndex + 1}/{total}</div>
            </div>
            <PlayerChip name={opponent?.name} score={scores.opp} side="opp" />
          </div>

          {/* timer */}
          <div className="flex justify-center">
            <div className={`font-mono font-bold text-lg px-4 py-1.5 rounded-full ${countdown <= 5 && phase === "match" ? "bg-su-danger/15 text-su-danger" : "bg-su-primary/10 text-su-primary"}`} data-testid="arena-timer">
              ⏱ {countdown}s
            </div>
          </div>

          {/* question */}
          {q && (
            <div className="card-chunky p-6">
              <p className="font-heading font-extrabold text-lg mb-5 whitespace-pre-line" data-testid="arena-question">{q.question_text}</p>
              <QStimulus q={q} />
              <div className="grid sm:grid-cols-2 gap-3">
                {q.options.map((o) => {
                  const isSel = selected === o.key;
                  const isCorrect = reveal && reveal.correct === o.key;
                  const isWrongSel = reveal && isSel && reveal.correct !== o.key;
                  return (
                    <button key={o.key} data-testid={`arena-option-${o.key}`} disabled={!!selected || !!reveal}
                      onClick={() => answer(o.key)}
                      className={`text-left flex items-center gap-3 rounded-2xl border-2 p-3.5 transition-all ${
                        isCorrect ? "border-su-success bg-su-success/15" : isWrongSel ? "border-su-danger bg-su-danger/15" :
                        isSel ? "border-su-primary bg-su-primary/10" : "border-su-border hover:border-su-primary/50"
                      } disabled:cursor-default`}>
                      <span className={`w-8 h-8 shrink-0 grid place-items-center rounded-lg font-heading font-black ${isSel || isCorrect ? "bg-su-primary text-white" : "bg-su-track text-su-muted"}`}>{o.key}</span>
                      {hasMedia(o) ? <OptMedia opt={o} /> : <span className="font-medium text-sm">{o.text}</span>}
                      {isCorrect && <Check className="w-5 h-5 text-su-success ml-auto" />}
                      {isWrongSel && <X className="w-5 h-5 text-su-danger ml-auto" />}
                    </button>
                  );
                })}
              </div>
              {selected && !reveal && <p className="text-center text-su-muted font-bold text-sm mt-4 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Menunggu lawan...</p>}
            </div>
          )}
        </div>
      )}

      {phase === "result" && result && (
        <div className="max-w-lg mx-auto">
          {result.outcome === "win" && <Confetti />}
          <div className="card-chunky p-8 text-center animate-pop">
            <div className={`w-24 h-24 rounded-3xl grid place-items-center mx-auto mb-4 ${result.outcome === "win" ? "bg-su-secondary/20" : result.outcome === "draw" ? "bg-su-primary/15" : "bg-su-danger/15"}`}>
              <Trophy className={`w-12 h-12 ${result.outcome === "win" ? "text-su-secondary" : result.outcome === "draw" ? "text-su-primary" : "text-su-danger"}`} />
            </div>
            <h2 className="font-heading font-black text-3xl mb-1" data-testid="arena-result">
              {result.outcome === "win" ? "MENANG! 🎉" : result.outcome === "draw" ? "SERI!" : "KALAH"}
            </h2>
            <p className="text-su-muted font-medium mb-4">Skor {result.your_score} — {result.opp_score}</p>
            <div className="inline-flex items-center gap-2 bg-su-primary/10 text-su-primary font-heading font-black px-5 py-2.5 rounded-full mb-6">
              <Zap className="w-4 h-4" /> {result.elo_change >= 0 ? "+" : ""}{result.elo_change} ELO → {result.new_elo}
            </div>
            <div className="flex gap-3">
              <Btn data-testid="arena-rematch" onClick={findMatch} className="flex-1">Main Lagi</Btn>
              <Link to="/dashboard" className="flex-1"><Btn variant="ghost" className="w-full">Dashboard</Btn></Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlayerChip({ name, score, side }) {
  return (
    <div className={`flex flex-col items-center ${side === "opp" ? "text-right" : "text-left"}`}>
      <div className={`w-12 h-12 rounded-2xl grid place-items-center font-heading font-black text-white mb-1 ${side === "you" ? "bg-su-primary" : "bg-su-danger"}`}>
        {name?.[0] || "?"}
      </div>
      <span className="font-heading font-extrabold text-sm truncate max-w-[90px]">{name || "..."}</span>
      <span className="font-mono font-bold text-2xl">{score ?? 0}</span>
    </div>
  );
}

function Confetti() {
  const colors = ["#1CB0F6", "#58CC02", "#FFC800", "#FF4B4B", "#FF9600"];
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {Array.from({ length: 50 }).map((_, i) => (
        <motion.div key={i}
          initial={{ y: -40, x: `${Math.random() * 100}vw`, rotate: 0 }}
          animate={{ y: "110vh", rotate: 720 * (Math.random() > 0.5 ? 1 : -1) }}
          transition={{ duration: 2 + Math.random(), delay: Math.random() * 0.4, ease: "easeIn" }}
          style={{ position: "absolute", width: 10, height: 14, background: colors[i % colors.length], borderRadius: 3 }} />
      ))}
    </div>
  );
}
